import { useEffect, useRef, useState } from "react";
import { Loader2, Save, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, apiError } from "@/lib/api";

const CONTACT_FIELDS = [
  { key: "phone", label: "Numéro de téléphone", testid: "settings-phone" },
  { key: "email", label: "Adresse e-mail (contact public)", testid: "settings-email" },
  { key: "owner_email", label: "E-mail de notification des nouveaux devis", testid: "settings-owner-email" },
  { key: "zone", label: "Zone d'intervention", testid: "settings-zone" },
  { key: "facebook", label: "Lien Facebook (facultatif)", testid: "settings-facebook" },
  { key: "instagram", label: "Lien Instagram (facultatif)", testid: "settings-instagram" },
];

const HERO_TEXT_FIELDS = [
  { key: "hero_kicker", label: "Sur-titre", testid: "settings-hero-kicker" },
  { key: "hero_title", label: "Titre (H1)", testid: "settings-hero-title" },
  { key: "hero_subtitle", label: "Ligne savoir-faire", testid: "settings-hero-subtitle" },
  { key: "hero_tagline", label: "Phrase d'accompagnement", testid: "settings-hero-tagline" },
  { key: "hero_cta", label: "Texte du bouton CTA", testid: "settings-hero-cta" },
];

const POSITIONS = [
  ["centre", "Centre"],
  ["haut", "Haut"],
  ["bas", "Bas"],
  ["gauche", "Gauche"],
  ["droite", "Droite"],
];

const inputCls =
  "w-full rounded-xl border border-line bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

export default function SiteSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Paramètres enregistrés — visibles immédiatement sur le site");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (slot, file) => {
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("slot", slot);
      fd.append("file", file);
      const { data } = await api.post("/admin/hero-photo", fd);
      setForm((f) => ({ ...f, [`hero_photo_${slot}`]: data.url }));
      toast.success("Photo du hero mise à jour");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading("");
    }
  };

  const deletePhoto = async (slot) => {
    try {
      await api.delete(`/admin/hero-photo/${slot}`);
      setForm((f) => ({ ...f, [`hero_photo_${slot}`]: "" }));
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const PhotoSlot = ({ slot, title, withLabel = false, withMeta = true }) => {
    const inputRef = useRef(null);
    const url = form[`hero_photo_${slot}`] || "";
    return (
      <div className="rounded-2xl border border-line p-5" data-testid={`hero-photo-${slot}`}>
        <h3 className="font-semibold text-sm text-ink">{title}</h3>
        <div className="mt-3 relative aspect-[16/10] rounded-xl overflow-hidden bg-sand border border-dashed border-line grid place-items-center">
          {url ? (
            <img src={imgUrl(url)} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs text-muted2 px-4 text-center">Aucune photo — utilisez une vraie réalisation MARY</span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          data-testid={`hero-photo-${slot}-input`}
          onChange={(e) => {
            if (e.target.files[0]) uploadPhoto(slot, e.target.files[0]);
            e.target.value = "";
          }}
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === slot}
            data-testid={`hero-photo-${slot}-upload`}
            className="inline-flex items-center gap-2 rounded-full bg-forest text-cream text-xs font-semibold px-4 py-2 hover:bg-pine transition-colors disabled:opacity-60"
          >
            {uploading === slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
            {url ? "Remplacer la photo" : "Ajouter une photo"}
          </button>
          {url && (
            <button
              type="button"
              onClick={() => deletePhoto(slot)}
              data-testid={`hero-photo-${slot}-delete`}
              className="inline-flex items-center gap-1.5 rounded-full border border-line text-xs font-semibold text-red-600 px-4 py-2 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
          )}
        </div>
        {withMeta && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            {withLabel && (
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-body2 mb-1.5">Label (affiché sur mobile)</label>
              <input
                value={form[`hero_photo_${slot}_label`] || ""}
                onChange={(e) => setForm({ ...form, [`hero_photo_${slot}_label`]: e.target.value })}
                placeholder="Ex. : Remise en état"
                data-testid={`hero-photo-${slot}-label`}
                className={inputCls}
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-body2 mb-1.5">Position / cadrage</label>
            <select
              value={form[`hero_photo_${slot}_position`] || "centre"}
              onChange={(e) => setForm({ ...form, [`hero_photo_${slot}_position`]: e.target.value })}
              data-testid={`hero-photo-${slot}-position`}
              className={inputCls}
            >
              {POSITIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-body2 mb-1.5">Texte alternatif (alt)</label>
            <input
              value={form[`hero_photo_${slot}_alt`] || ""}
              onChange={(e) => setForm({ ...form, [`hero_photo_${slot}_alt`]: e.target.value })}
              placeholder="Description de la photo"
              data-testid={`hero-photo-${slot}-alt`}
              className={inputCls}
            />
          </div>
        </div>
        )}
      </div>
    );
  };

  if (!form) return <p className="text-sm text-muted2">Chargement…</p>;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display font-semibold text-2xl sm:text-3xl text-ink">Site</h1>
      <p className="mt-1 text-sm text-muted2">Modifiez les contenus du site sans toucher au code. Le design reste verrouillé.</p>

      <form onSubmit={save} className="mt-8 max-w-3xl space-y-6" data-testid="settings-form">
        <section className="rounded-2xl bg-white border border-line p-6 sm:p-8">
          <h2 className="font-display font-semibold text-lg text-ink">Hero — contenu</h2>
          <div className="mt-5 space-y-5">
            {HERO_TEXT_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-semibold text-body2 mb-1.5">{f.label}</label>
                <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={f.testid} className={inputCls} />
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-line p-6 sm:p-8">
          <h2 className="font-display font-semibold text-lg text-ink">Hero — photo</h2>
          <p className="mt-1 text-xs text-muted2">Utilisez une vraie réalisation MARY. L'image est optimisée automatiquement (WebP, HEIC accepté).</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 max-w-3xl">
            <PhotoSlot slot="before" title="Photo AVANT (début du défilement)" />
            <PhotoSlot slot="after" title="Photo APRÈS (fin du défilement)" />
          </div>
          <p className="mt-3 text-xs text-muted2 max-w-xl">
            Sur ordinateur, la photo reste fixe en haut de page et se transforme d'AVANT en APRÈS au fil du défilement. Sur mobile, la photo APRÈS est affichée (ou la photo mobile ci-dessous si activée).
          </p>
          <div className="mt-6">
            <label className="flex items-center gap-3 text-sm text-body2" data-testid="hero-mobile-toggle">
              <input
                type="checkbox"
                checked={form.hero_photo_mobile_enabled === "true"}
                onChange={(e) => setForm({ ...form, hero_photo_mobile_enabled: e.target.checked ? "true" : "false" })}
                className="h-4 w-4 accent-[#1B3B2B]"
              />
              Utiliser une photo spécifique sur mobile
            </label>
            {form.hero_photo_mobile_enabled === "true" && (
              <div className="mt-4 max-w-md">
                <PhotoSlot slot="mobile" title="Photo mobile" />
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-line p-6 sm:p-8">
          <h2 className="font-display font-semibold text-lg text-ink">Photos des sections</h2>
          <p className="mt-1 text-xs text-muted2">Remplacez les visuels de la section Particuliers / Professionnels par vos propres photos. Les photos des cartes Services se gèrent dans le menu « Services ».</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <PhotoSlot slot="audience_particuliers" title="Section Particuliers" withMeta={false} />
            <PhotoSlot slot="audience_pro" title="Section Professionnels" withMeta={false} />
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-line p-6 sm:p-8 space-y-5">
          <h2 className="font-display font-semibold text-lg text-ink">Coordonnées</h2>
          {CONTACT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-body2 mb-1.5">{f.label}</label>
              <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={f.testid} className={inputCls} />
            </div>
          ))}
        </section>

        <button type="submit" disabled={saving} data-testid="settings-save" className="rounded-full bg-forest text-cream font-semibold px-6 py-3 text-sm hover:bg-pine transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer
        </button>
      </form>
    </div>
  );
}
